/**
 * PWA Service Worker Registration & Update Handler
 *
 * This module handles:
 * - Service worker registration
 * - Automatic updates detection
 * - Prompting users when a new version is available
 * - Offline/online status events
 */

export function registerServiceWorker() {
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", async () => {
      try {
        const registration = await navigator.serviceWorker.register(
          "/sw.js",
          { scope: "/" }
        );

        // Check for updates periodically (every 60 minutes)
        setInterval(() => {
          registration.update();
        }, 1000 * 60 * 60);

        // Handle updates
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // New content available — notify user
              showUpdateNotification(registration);
            }
          });
        });

        console.log("[PWA] Service worker registered successfully");
      } catch (error) {
        console.error("[PWA] Service worker registration failed:", error);
      }
    });

    // Listen for controller change (when new SW takes over)
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      // Reload the page to load new assets
      window.location.reload();
    });
  }
}

/**
 * Show a toast/banner prompting the user to update
 */
function showUpdateNotification(registration) {
  // Create a simple update banner
  const banner = document.createElement("div");
  banner.id = "pwa-update-banner";
  banner.innerHTML = `
    <div style="
      position: fixed;
      bottom: 24px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 99999;
      background: #0099C4;
      color: white;
      padding: 14px 20px;
      border-radius: 14px;
      box-shadow: 0 8px 32px rgba(0, 153, 196, 0.3);
      display: flex;
      align-items: center;
      gap: 12px;
      font-family: Inter, system-ui, sans-serif;
      font-size: 14px;
      max-width: 400px;
      width: calc(100% - 32px);
      animation: slideUpBanner 300ms cubic-bezier(0.16, 1, 0.3, 1);
    ">
      <span style="flex: 1;">A new version is available!</span>
      <button id="pwa-update-btn" style="
        background: white;
        color: #0099C4;
        border: none;
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        white-space: nowrap;
      ">Update</button>
      <button id="pwa-dismiss-btn" style="
        background: transparent;
        color: white;
        border: none;
        padding: 4px;
        cursor: pointer;
        opacity: 0.7;
        font-size: 18px;
        line-height: 1;
      ">&times;</button>
    </div>
    <style>
      @keyframes slideUpBanner {
        from { opacity: 0; transform: translateX(-50%) translateY(20px); }
        to { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
    </style>
  `;

  document.body.appendChild(banner);

  // Update button — skip waiting and reload
  document.getElementById("pwa-update-btn").addEventListener("click", () => {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    banner.remove();
  });

  // Dismiss button
  document.getElementById("pwa-dismiss-btn").addEventListener("click", () => {
    banner.remove();
  });
}

/**
 * Online/Offline status listener
 * Dispatches custom events that components can listen to
 */
export function initConnectivityListener() {
  const dispatchStatus = (isOnline) => {
    window.dispatchEvent(
      new CustomEvent("connectivity-change", { detail: { isOnline } })
    );
  };

  window.addEventListener("online", () => dispatchStatus(true));
  window.addEventListener("offline", () => dispatchStatus(false));
}
